import { type TrackingObject } from "./models";
import { createPostponeQueue } from "./postpone";

interface TrackingMethods {
    trackUserCreated: () => void;
    trackUserRegistration: () => void;
    trackOrder: (data: any) => void;
    trackLandingPageFormula: () => void;
    trackCompanyRegistration: () => void;
    trackCompanyRegistrationRedirect: () => void;
    trackDonationMade: () => void;
    trackAffiliateRegistration: () => void;
    trackCauseRegistration: () => void;
    trackAffiliateRegistrationRedirect: () => void;
    trackCauseRegistrationRedirect: () => void;
    trackBid: () => void;
    trackBidRedirect: () => void;
    trackBuyNow: () => void;
    trackPageView: () => void;
    trackProductView: (product: {
        productId: number;
        productName: string;
        categoryPath: string[] | undefined | null;
        shop: boolean;
    }) => void;
    trackProductList: (product: any, listName: any, categoryPath: any) => void;
    trackCustomGoogleAnalyticsEvent: (
        category: any,
        action: any,
        label: any,
        value: any
    ) => void;
    trackAddToCart: (sku: string) => void;
    trackRemoveFromCart: (sku: string) => void;
}

interface ITrackingServiceMethods extends TrackingMethods {
    setUserLoggedIn: () => void;
    storeValuesForProductView: (
        productId: any,
        list: any,
        categoryPath: any,
        breadcrumb: any
    ) => void;
    getStoredValuesForProductLoad: (productId: any) => any;
    joinCategoryPath: (categoryPath: any) => string;
    setCustomGoogleAnalyticsVars: (
        slot: any,
        name: any,
        value: any,
        scope: any
    ) => void;
    postpone(promise: Promise<any>): void;
}

interface SessionTrackArguments {}

export const { sessionTrackOnLoad, fireStoredEvents } = (function (g) {
    const prefix = "auc_load_Tracker_";
    const lockKey = prefix + "lock";
    const dataArrayKey = prefix + "dataarray";

    const storage = sessionStorage;

    const parseData = function <TType>(d: string | null, fallback: TType) {
        return d == null ? fallback : (JSON.parse(d) as TType);
    };

    const storeTrackingAction = function (func: (da: any[]) => void) {
        let retries = 100;
        let lastValue: string | null = "true";
        while (lastValue === "true" && retries-- > 0)
            lastValue = storage.getItem(lockKey);

        if (lastValue === "true")
            throw new Error("Could not access content, because lock was true");

        try {
            storage.setItem(lockKey, "true");
            const dataArray = parseData(storage.getItem(dataArrayKey), []);
            func(dataArray);
            storage.setItem(dataArrayKey, JSON.stringify(dataArray));
        } finally {
            storage.setItem(lockKey, "false");
        }
    };

    const track = function (data: SessionTrackArguments) {
        storeTrackingAction(function (da) {
            da.push(data);
        });
    };

    const fireStoredEvents = function () {
        storeTrackingAction(function (dataArray) {
            while (dataArray.length > 0) {
                const item = dataArray.shift();
                if (item == null) continue;

                const target = ((g as any)[item.identifier] =
                    (g as any)[item.identifier] || []);

                if (item.method === "push") target.push(item.parameters);
                else console.warn("Not implemented method" + item.method);
            }
        });
    };

    g.document.addEventListener("DOMContentLoaded", fireStoredEvents);

    return {
        sessionTrackOnLoad: function (options: SessionTrackArguments) {
            track(options);
        },
        fireStoredEvents: fireStoredEvents,
    };
})(window);

// eslint-disable-next-line no-unused-vars
export const odendoTrackingService = (function () {
    const gtmEnabled =
        (window as any).odendoTrackingService_init_gtmEnabled === true;
    const trackGaq = !gtmEnabled;
    const trackFbq = !gtmEnabled;
    const trackGtm = gtmEnabled;

    const ensureArray = function (name: string) {
        if (window.hasOwnProperty(name)) {
            const value = (window as any)[name];
            if (value != undefined) {
                if (Array.isArray(value)) return value;
                if (typeof value.push === "function")
                    return value as { push(obj: any): void };
                console.warn("window." + name + " is defined but not as array");
                return value as any[];
            }
        }
        console.warn("Declaring " + name);
        const value = ((window as any)[name] = [] as any[]);
        return value;
    };

    const storeNextLoad = (function () {
        const prefix = "snl_";
        return {
            dequeue: function (name: string) {
                console.debug("DEQUEUE ITEM: " + name);
                const key = prefix + name;
                const value = localStorage.getItem(key);
                if (value == null) {
                    return value;
                }
                localStorage.removeItem(key);
                return JSON.parse(value);
            },
            queue: function (name: string, value: any) {
                console.debug("QUEUE ITEM: " + name);
                const key = prefix + name;
                const val = JSON.stringify(value);
                localStorage.setItem(key, val);
            },
        };
    })();

    const queue = createPostponeQueue();

    if ("initialOdendoPostpone" in window) {
        if (
            Array.isArray(window["initialOdendoPostpone"]) &&
            window["initialOdendoPostpone"].length > 0
        ) {
            const promises = window["initialOdendoPostpone"];
            queue.postpone(Promise.all(promises));
        }
    }

    const trackingMethodsPart: TrackingMethods = {
        trackUserCreated: function () {
            if (trackGaq)
                ensureArray("_gaq").push([
                    "_trackEvent",
                    "New user created",
                    "New user created",
                ]);
            if (trackGtm)
                ensureArray("dataLayer").push({
                    event: "New user created",
                    eventAction: "New user created",
                });
        },
        trackUserRegistration: function () {
            if (trackFbq)
                ensureArray("_fbq").push(["track", "CompleteRegistration"]);
        },
        trackOrder: function (data: TrackingObject) {
            let trns;

            if (trackGaq) {
                trns = data.transaction;

                const gaq = ensureArray("_gaq");
                gaq.push([
                    "_addTrans",
                    data.orderNumber,
                    trns.storeName,
                    trns.amount.toString(),
                    trns.tax.toString(),
                    trns.shipping.toString(),
                ]);

                for (let i = 0; i < data.items.length; i++) {
                    const item = data.items[i];
                    gaq.push([
                        "_addItem",
                        data.orderNumber,
                        item.sku,
                        item.name,
                        item.category,
                        item.amount.toString(),
                        item.quantity.toString(),
                    ]);
                }

                gaq.push(["_trackTrans"]);
            }
            if (trackGtm) {
                trns = data.transaction;

                const products = data.items.map(function (item: any) {
                    return {
                        id: item.sku,
                        name: item.name,
                        category: item.category,
                        quantity: item.quantity.toString(),
                        price: item.amount.toString(),
                    };
                });

                let orderNumber = data.orderNumber.toString();

                const prefix = data.orderNumberPrefix;
                if (typeof prefix === "string" && prefix.length > 0) {
                    orderNumber = prefix + "-" + orderNumber;
                }

                console.log("TRACK " + orderNumber);

                ensureArray("dataLayer").push({
                    event: "purchase",
                    ecommerce: {
                        currencyCode:
                            (window as any)
                                .odendoTrackingService_init_currencyCode ?? "",
                        purchase: {
                            actionField: {
                                storeName: trns.storeName,
                                id: orderNumber,
                                revenue: trns.amount.toString(),
                                revenue2: trns.amount,
                                revenueExVat: trns.amountExVat.toString(),
                                revenueExVat2: trns.amountExVat,
                                tax: trns.tax.toString(),
                                shipping: trns.shipping.toString(),
                                couponCode: data.couponCode,
                                emailAddress: data.emailAddress,
                                currencyIsoCode: trns.currencyCode,
                                billingAddress: data.billingAddress,
                            },
                            products: products,
                        },
                    },
                });

                console.debug("trackOrder - gtm", ensureArray("dataLayer"));
            }
        },
        trackLandingPageFormula: function () {
            if (trackGaq)
                ensureArray("_gaq").push([
                    "_trackEvent",
                    "Submitted landingpage form",
                    "Submitted landingpage form",
                ]);
            if (trackGtm)
                ensureArray("dataLayer").push({
                    event: "Submitted landingpage form",
                    eventAction: "Submitted landingpage form",
                });
        },
        trackCompanyRegistration: function () {
            if (trackGaq)
                ensureArray("_gaq").push([
                    "_trackEvent",
                    "Company registration",
                    "Company registration",
                ]);
            if (trackGtm)
                ensureArray("dataLayer").push({
                    event: "Company registration",
                    eventAction: "Company registration",
                });
        },
        trackCompanyRegistrationRedirect: function () {
            if (trackGaq)
                sessionTrackOnLoad({
                    identifier: "_gaq",
                    method: "push",
                    parameters: [
                        "_trackEvent",
                        "Company registration",
                        "Company registration",
                    ],
                });
            if (trackGtm)
                sessionTrackOnLoad({
                    identifier: "dataLayer",
                    method: "push",
                    parameters: {
                        event: "Company registration",
                        eventAction: "Company registration",
                    },
                });
        },
        trackDonationMade: function () {
            if (trackGaq)
                ensureArray("_gaq").push([
                    "_trackEvent",
                    "Donation made",
                    "Donation made",
                ]);
            if (trackGtm)
                ensureArray("dataLayer").push({
                    event: "Donation made",
                    eventAction: "Donation made",
                });
        },
        trackAffiliateRegistration: function () {
            if (trackGaq)
                ensureArray("_gaq").push([
                    "_trackEvent",
                    "Affiliate registration",
                    "Affiliate registration",
                ]);
            if (trackGtm)
                ensureArray("dataLayer").push({
                    event: "Affiliate registration",
                    eventAction: "Affiliate registration",
                });
        },
        trackCauseRegistration: function () {
            if (trackGaq)
                ensureArray("_gaq").push([
                    "_trackEvent",
                    "Cause registration",
                    "Cause registration",
                ]);
            if (trackGtm)
                ensureArray("dataLayer").push({
                    event: "Cause registration",
                    eventAction: "Cause registration",
                });
        },
        trackAffiliateRegistrationRedirect: function () {
            if (trackGaq)
                sessionTrackOnLoad({
                    identifier: "_gaq",
                    method: "push",
                    parameters: [
                        "_trackEvent",
                        "Affiliate registration",
                        "Affiliate registration",
                    ],
                });
            if (trackGtm)
                sessionTrackOnLoad({
                    identifier: "dataLayer",
                    method: "push",
                    parameters: {
                        event: "Affiliate registration",
                        eventAction: "Affiliate registration",
                    },
                });
        },
        trackCauseRegistrationRedirect: function () {
            if (trackGaq)
                sessionTrackOnLoad({
                    identifier: "_gaq",
                    method: "push",
                    parameters: [
                        "_trackEvent",
                        "Cause registration",
                        "Cause registration",
                    ],
                });
            if (trackGtm)
                sessionTrackOnLoad({
                    identifier: "dataLayer",
                    method: "push",
                    parameters: {
                        event: "Cause registration",
                        eventAction: "Cause registration",
                    },
                });
        },
        trackBid: function () {
            if (trackGaq)
                ensureArray("_gaq").push([
                    "_trackEvent",
                    "Bid made",
                    "Bid made",
                ]);
            if (trackFbq) ensureArray("_fbq").push(["track", "Lead"]);
            if (trackGtm)
                ensureArray("dataLayer").push({
                    event: "Bid made",
                    eventAction: "Bid made",
                });
        },
        trackBidRedirect: function () {
            if (trackGaq)
                sessionTrackOnLoad({
                    identifier: "_gaq",
                    method: "push",
                    parameters: ["_trackEvent", "Bid made", "Bid made"],
                });
            if (trackFbq)
                sessionTrackOnLoad({
                    identifier: "_fbq",
                    method: "push",
                    parameters: ["track", "Lead"],
                });
            if (trackGtm)
                sessionTrackOnLoad({
                    identifier: "dataLayer",
                    method: "push",
                    parameters: { event: "Bid made", eventAction: "Bid made" },
                });
        },
        trackBuyNow: function () {
            if (trackGaq)
                sessionTrackOnLoad({
                    identifier: "_gaq",
                    method: "push",
                    parameters: ["_trackEvent", "Buynow made", "Buynow made"],
                });
            if (trackFbq)
                sessionTrackOnLoad({
                    identifier: "_fbq",
                    method: "push",
                    parameters: ["track", "Purchase"],
                });
            if (trackGtm)
                sessionTrackOnLoad({
                    identifier: "dataLayer",
                    method: "push",
                    parameters: {
                        event: "Buynow made",
                        eventAction: "Buynow made",
                    },
                });
        },
        trackPageView: function () {
            console.debug("PAGEVIEW");
            if (trackGaq) ensureArray("_gaq").push(["_trackPageview"]);
        },
        trackProductView: function (product) {
            let listName = "";
            let categoryPath = null;

            const storedValues = getStoredValuesForProductLoad(
                product.productId
            );
            if (storedValues != null) {
                if (storedValues.hasOwnProperty("list")) {
                    listName = storedValues.list;
                }
                if (storedValues.hasOwnProperty("categoryPath")) {
                    categoryPath = storedValues.categoryPath;
                }
                if (
                    window.history != null &&
                    typeof window.history.replaceState == "function"
                ) {
                    if (
                        window.history.state == null ||
                        window.history.state.hasOwnProperty("storedValues") ==
                            false
                    )
                        window.history.replaceState(
                            Object.assign({}, window.history.state, {
                                storedValues,
                            }),
                            ""
                        );
                }
            }

            if (
                categoryPath == null ||
                !Array.isArray(categoryPath) ||
                categoryPath.length === 0
            ) {
                categoryPath = product.categoryPath;
            }

            if (trackGtm) {
                const category = joinCategoryPath(categoryPath);

                ensureArray("dataLayer").push({
                    event: "detailView",
                    ecommerce: {
                        detail: {
                            actionField: {
                                list: listName,
                            },
                            products: [
                                {
                                    id: product.productId.toString(),
                                    name: product.productName,
                                    shop: product.shop,
                                    category: category,
                                },
                            ],
                        },
                    },
                });
            }
        },
        trackProductList: function (products, listName, categoryPath) {
            if (trackGtm) {
                const category = joinCategoryPath(categoryPath);

                const impressions = products.map((product: any) => {
                    let index = product.index;
                    if (typeof index == "number" && !isNaN(index)) index += 1;
                    let cat = category;
                    if (cat === "") {
                        cat = joinCategoryPath(product.categoryPath);
                    }
                    return {
                        id: product.productId.toString(),
                        name: product.productName,
                        category: cat,
                        list: listName,
                        position: index,
                    };
                });
                ensureArray("dataLayer").push({
                    event: "impressionView",
                    ecommerce: {
                        impressions: impressions,
                    },
                });
            }
        },
        trackCustomGoogleAnalyticsEvent(category, action, label, value) {
            if (trackGaq) {
                ensureArray("_gaq").push([
                    "_trackEvent",
                    category,
                    action,
                    label,
                    value,
                ]);
            }
            if (trackGtm) {
                ensureArray("dataLayer").push({
                    event: category,
                    eventAction: action,
                    eventLabel: label,
                });
            }
        },
        trackAddToCart(sku: string) {
            if (trackGtm) {
                console.log("TRACK ADD " + sku);
                ensureArray("dataLayer").push({
                    event: "addToCart",
                    ecommerce: {
                        detail: {
                            actionField: {
                                sku: sku,
                            },
                        },
                    },
                });
            }
        },
        trackRemoveFromCart(sku: string) {
            if (trackGtm) {
                console.log("TRACK REMOVE " + sku);
                ensureArray("dataLayer").push({
                    event: "removeFromCart",
                    ecommerce: {
                        detail: {
                            actionField: {
                                sku: sku,
                            },
                        },
                    },
                });
            }
        },
    };

    for (const property in trackingMethodsPart) {
        const originalFunction = (trackingMethodsPart as any)[property];

        (trackingMethodsPart as any)[property] = function (...args: any[]) {
            return queue.execute(function () {
                originalFunction.apply(trackingMethodsPart, args);
            });
        };
    }

    const getStoredValuesForProductLoad = function (productId: number) {
        if (
            window.history != null &&
            window.history.state != null &&
            window.history.state.hasOwnProperty("storedValues")
        )
            return window.history.state.storedValues;
        const storedValues = storeNextLoad.dequeue("productView_" + productId);
        history.replaceState({ ...history.state, storedValues }, "");
        return storedValues;
    };

    const joinCategoryPath = function (categoryPath: any) {
        if (categoryPath == null) return "";
        if (!Array.isArray(categoryPath)) return "";
        if (categoryPath.length === 0) return "";
        return categoryPath.join("/");
    };

    const trackingFunctions = {
        ...trackingMethodsPart,
        postpone(promise) {
            queue.postpone(promise);
        },
        setUserLoggedIn: function () {
            if (trackGaq)
                ensureArray("_gaq").push([
                    "_setCustomVar",
                    1,
                    "UserType",
                    "Logged in",
                    2,
                ]);
        },
        storeValuesForProductView: function (
            productId,
            list,
            categoryPath,
            breadcrumb
        ) {
            storeNextLoad.queue("productView_" + productId, {
                list: list,
                categoryPath: categoryPath,
                breadcrumb: breadcrumb,
            });
        },
        getStoredValuesForProductLoad: function (productId) {
            return getStoredValuesForProductLoad(productId);
        },
        joinCategoryPath(categoryPath) {
            return joinCategoryPath(categoryPath);
        },

        setCustomGoogleAnalyticsVars(slot, name, value, scope) {
            if (trackGaq) {
                ensureArray("_gaq").push([
                    "_setCustomVar",
                    slot,
                    name,
                    value,
                    scope,
                ]);
            }
            if (trackGtm) {
                const dimensionName = "dimension" + slot;
                const trackObj = {
                    event: dimensionName,
                    varName: name,
                    varScope: scope,
                };
                (trackObj as any)[dimensionName] = value;

                ensureArray("dataLayer").push(trackObj);
            }
        },
    } as ITrackingServiceMethods;

    const resultObj: { ensureArray?: typeof ensureArray } = {};
    resultObj.ensureArray = function (name: string) {
        return ensureArray(name);
    };

    const makeFunction = function <This, Result, TArgs extends []>(
        fnc: (this: This, ...args: TArgs) => Result
    ) {
        return function (this: This, ...args: TArgs) {
            return fnc.apply(this, args);
        };
    };

    const trackingFunctionNames = Object.keys(
        trackingFunctions
    ) as (keyof ITrackingServiceMethods)[];
    for (let i = 0; i < trackingFunctionNames.length; i++) {
        const trackingFunctionName = trackingFunctionNames[i];
        if (resultObj.hasOwnProperty(trackingFunctionName)) {
            console.warn(
                "Skipped a tracking function that already existed in resultObj: " +
                    trackingFunctionName
            );
            continue;
        }
        Object.defineProperty(resultObj, trackingFunctionName, {
            value: makeFunction(
                (trackingFunctions as any)[trackingFunctionName]
            ),
        });
    }

    return resultObj as typeof resultObj & ITrackingServiceMethods;
})();
